import { Routes, Route, Navigate, useLocation, type Location } from 'react-router-dom'
import { useEffect, Suspense, lazy, useRef, useMemo, Component, type ComponentType, type ReactNode, type ErrorInfo } from 'react'
import { Sidebar } from './components/common/Sidebar'
import { TopBar } from './components/common/TopBar'
import { CommandPalette } from './components/common/CommandPalette'
import SupportChatBubble from './components/common/SupportChatBubble'
import { SupportChatProvider } from './hooks/useSupportChat'
import { useAppStore, applyBranding } from './store/app'
import { useKeycloakAuth, keycloakLogout } from './hooks/useKeycloak'
import { usePortalAuth } from './hooks/usePortalAuth'
import { decodeIdentity } from '@/lib/jwtAuth'
import { getPortalSlugFromHost } from '@/utils/portalHost'
import { brandingApi } from './api/branding'
import { iamService } from './api/iam'
import { iamAdminApi } from './api/iamAdmin'
import { useRole } from './hooks/useRole'
import { isSubscribedPath } from '@/utils/permissions'
import PortalLogin from './pages/portal/PortalLogin'
import { PortalAuthContext, PortalLayoutRoute } from './pages/portal/PortalLayout'

// Wraps React.lazy so a stale chunk reference (browser tab open from before
// the latest deploy, requesting a hashed filename that no longer exists on
// the server) triggers one full-page reload to pick up the new build, instead
// of surfacing "Failed to fetch dynamically imported module" as a dead page.
function lazyWithRetry<T extends { default: ComponentType<any> }>(loader: () => Promise<T>) {
  return lazy(async () => {
    try {
      return await loader()
    } catch (err) {
      const key = 'chunk-reload-retried'
      if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, '1')
        window.location.reload()
        return new Promise<T>(() => {}) // reloading — never resolve, avoid flashing an error
      }
      sessionStorage.removeItem(key)
      throw err
    }
  })
}

// Pages — lazy-loaded so each route ships its own chunk instead of bloating
// every page's bundle with every other page's code (Dashboards' chart panels,
// Billing, RBAC, etc. were previously all loaded just to view the Tickets page).
const Login              = lazyWithRetry(() => import('./pages/Login'))
const Home               = lazyWithRetry(() => import('./pages/Home'))
const Dashboards         = lazyWithRetry(() => import('./pages/Dashboards'))
const DashboardView      = lazyWithRetry(() => import('./pages/DashboardView'))
const AIInsights         = lazyWithRetry(() => import('./pages/AIInsights'))
const AlertRules         = lazyWithRetry(() => import('./pages/AlertRules'))
const AlertChannels      = lazyWithRetry(() => import('./pages/AlertChannels'))
const IncidentHistory    = lazyWithRetry(() => import('./pages/IncidentHistory'))
const LogsExplorer       = lazyWithRetry(() => import('./pages/LogsExplorer'))
const TraceExplorer      = lazyWithRetry(() => import('./pages/TraceExplorer'))
const MetricsExplorer    = lazyWithRetry(() => import('./pages/MetricsExplorer'))
const QueryBuilder       = lazyWithRetry(() => import('./pages/QueryBuilder'))
const AnalyticsBuilder   = lazyWithRetry(() => import('./pages/AnalyticsBuilder'))
const Correlations       = lazyWithRetry(() => import('./pages/Correlations'))
const CorrelationRules   = lazyWithRetry(() => import('./pages/CorrelationRules'))
const GroupedIncidents   = lazyWithRetry(() => import('./pages/GroupedIncidents'))
const PluginMarketplace  = lazyWithRetry(() => import('./pages/PluginMarketplace'))
const DatasourceManager  = lazyWithRetry(() => import('./pages/DatasourceManager'))
const Reports            = lazyWithRetry(() => import('./pages/Reports'))
const TeamCollaboration  = lazyWithRetry(() => import('./pages/TeamCollaboration'))
const Automation         = lazyWithRetry(() => import('./pages/Automation'))
const AuditLogs          = lazyWithRetry(() => import('./pages/AuditLogs'))
const Billing            = lazyWithRetry(() => import('./pages/Billing'))
const Settings           = lazyWithRetry(() => import('./pages/Settings'))
const Profile            = lazyWithRetry(() => import('./pages/Profile'))
const OnboardingWizard   = lazyWithRetry(() => import('./pages/OnboardingWizard'))
const Register           = lazyWithRetry(() => import('./pages/Register'))
const EnterpriseAuth     = lazyWithRetry(() => import('./pages/EnterpriseAuth'))
const OrganizationHub    = lazyWithRetry(() => import('./pages/OrganizationHub'))
const AdminAIProvider    = lazyWithRetry(() => import('./pages/AdminAIProvider'))
const WhiteLabel         = lazyWithRetry(() => import('./pages/WhiteLabel'))
const Slos               = lazyWithRetry(() => import('./pages/Slos'))
const SloDetail          = lazyWithRetry(() => import('./pages/SloDetail'))
const IncidentDetail     = lazyWithRetry(() => import('./pages/IncidentDetail'))
const Runbooks           = lazyWithRetry(() => import('./pages/Runbooks'))

// ── Business modules — replace stub with real implementation as each is ported
const Tickets             = lazyWithRetry(() => import('./pages/Tickets'))
const TicketDetail        = lazyWithRetry(() => import('./pages/TicketDetail'))
const TicketSettings      = lazyWithRetry(() => import('./pages/TicketSettings'))
const TicketAnalytics     = lazyWithRetry(() => import('./pages/TicketAnalytics'))
const LiveChat            = lazyWithRetry(() => import('./pages/LiveChat'))
const ScheduleTicket      = lazyWithRetry(() => import('./pages/ScheduleTicket'))
const TaskBoard           = lazyWithRetry(() => import('./pages/TaskBoard'))
const TaskPlanning        = lazyWithRetry(() => import('./pages/TaskPlanning'))
const Timesheets          = lazyWithRetry(() => import('./pages/Timesheets'))
const TimesheetApprovals  = lazyWithRetry(() => import('./pages/TimesheetApprovals'))
const Notes               = lazyWithRetry(() => import('./pages/Notes'))
const WasteOverview       = lazyWithRetry(() => import('./pages/waste/WasteOverview'))
const WasteCollections    = lazyWithRetry(() => import('./pages/waste/WasteCollections'))
const WasteFacilities     = lazyWithRetry(() => import('./pages/waste/WasteFacilities'))
const WasteFacilitators   = lazyWithRetry(() => import('./pages/waste/WasteFacilitators'))
const WasteSimulator      = lazyWithRetry(() => import('./pages/waste/WasteSimulator'))
const WasteOversight      = lazyWithRetry(() => import('./pages/waste/WasteOversight'))
const WasteReports        = lazyWithRetry(() => import('./pages/waste/WasteReports'))
const WasteSettings       = lazyWithRetry(() => import('./pages/waste/WasteSettings'))
const WastePickups        = lazyWithRetry(() => import('./pages/waste/WastePickups'))
const WasteAgencies       = lazyWithRetry(() => import('./pages/waste/WasteAgencies'))
const WasteMarketplace    = lazyWithRetry(() => import('./pages/waste/WasteMarketplace'))
const WasteRecyclers      = lazyWithRetry(() => import('./pages/waste/WasteRecyclers'))
const Clients             = lazyWithRetry(() => import('./pages/Clients'))
const ClientDetail        = lazyWithRetry(() => import('./pages/ClientDetail'))
const Invoices            = lazyWithRetry(() => import('./pages/Invoices'))
const InvoiceDetail       = lazyWithRetry(() => import('./pages/InvoiceDetail'))
const InvoiceCreate       = lazyWithRetry(() => import('./pages/InvoiceCreate'))
const InvoiceSettings     = lazyWithRetry(() => import('./pages/InvoiceSettings'))
const Assets              = lazyWithRetry(() => import('./pages/Assets'))
const AssetCategories     = lazyWithRetry(() => import('./pages/AssetCategories'))
const AssetLocations      = lazyWithRetry(() => import('./pages/AssetLocations'))
const AssetManufacturers  = lazyWithRetry(() => import('./pages/AssetManufacturers'))
const AssetVendors        = lazyWithRetry(() => import('./pages/AssetVendors'))
const AssetComponents     = lazyWithRetry(() => import('./pages/AssetComponents'))
const AssetAllocations    = lazyWithRetry(() => import('./pages/AssetAllocations'))
const AssetWorkflows      = lazyWithRetry(() => import('./pages/AssetWorkflows'))
const AssetSaaSApplications = lazyWithRetry(() => import('./pages/AssetSaaSApplications'))
const AssetCloudResources   = lazyWithRetry(() => import('./pages/AssetCloudResources'))
const AssetCredentials      = lazyWithRetry(() => import('./pages/AssetCredentials'))
const AssetIntegrations   = lazyWithRetry(() => import('./pages/AssetIntegrations'))
const AssetNotifications  = lazyWithRetry(() => import('./pages/AssetNotifications'))
const AssetAgents         = lazyWithRetry(() => import('./pages/AssetAgents'))
const AgentDeviceDetail   = lazyWithRetry(() => import('./pages/AgentDeviceDetail'))
const AssetAnalytics      = lazyWithRetry(() => import('./pages/AssetAnalytics'))
const AssetAudits         = lazyWithRetry(() => import('./pages/AssetAudits'))
const AssetAuditDetail    = lazyWithRetry(() => import('./pages/AssetAuditDetail'))
const AssetReorderPolicies = lazyWithRetry(() => import('./pages/AssetReorderPolicies'))
const AssetReportBuilder  = lazyWithRetry(() => import('./pages/AssetReportBuilder'))
const AssetProfiles       = lazyWithRetry(() => import('./pages/AssetProfiles'))
const ProcessWorkflows    = lazyWithRetry(() => import('./pages/ProcessWorkflows'))
const ProcessRuns         = lazyWithRetry(() => import('./pages/ProcessRuns'))
const AssetDetail         = lazyWithRetry(() => import('./pages/AssetDetail'))
const AssetGraph          = lazyWithRetry(() => import('./pages/AssetGraph'))
const AssetRequests       = lazyWithRetry(() => import('./pages/AssetRequests'))
const AssetSoftwareCatalog = lazyWithRetry(() => import('./pages/AssetSoftwareCatalog'))
const AssetScan           = lazyWithRetry(() => import('./pages/AssetScan'))
const AssetNetworkTopology = lazyWithRetry(() => import('./pages/AssetNetworkTopology'))
const Contracts           = lazyWithRetry(() => import('./pages/Contracts'))
const PurchaseOrders      = lazyWithRetry(() => import('./pages/PurchaseOrders'))
const AssetPurchaseOrderDetail = lazyWithRetry(() => import('./pages/AssetPurchaseOrderDetail'))
const PurchaseOrdersBiz   = lazyWithRetry(() => import('./pages/PurchaseOrdersBiz'))
const ProcurementPOCreate = lazyWithRetry(() => import('./pages/ProcurementPOCreate'))
const Quotations          = lazyWithRetry(() => import('./pages/Quotations'))
const QuotationCreate     = lazyWithRetry(() => import('./pages/QuotationCreate'))
const QuotationDetail     = lazyWithRetry(() => import('./pages/QuotationDetail'))
const ProformaInvoices    = lazyWithRetry(() => import('./pages/ProformaInvoices'))
const ProformaCreate      = lazyWithRetry(() => import('./pages/ProformaCreate'))
const ProformaDetail      = lazyWithRetry(() => import('./pages/ProformaDetail'))
const SalesOrders         = lazyWithRetry(() => import('./pages/SalesOrders'))
const SalesOrderCreate    = lazyWithRetry(() => import('./pages/SalesOrderCreate'))
const SalesOrderDetail    = lazyWithRetry(() => import('./pages/SalesOrderDetail'))
const PurchaseOrderCreate = lazyWithRetry(() => import('./pages/PurchaseOrderCreate'))
const PurchaseOrderDetail = lazyWithRetry(() => import('./pages/PurchaseOrderDetail'))
const KnowledgeBase       = lazyWithRetry(() => import('./pages/KnowledgeBase'))
const KBArticle           = lazyWithRetry(() => import('./pages/KBArticle'))
const KBArticleEditor     = lazyWithRetry(() => import('./pages/KBArticleEditor'))
const Help                = lazyWithRetry(() => import('./pages/Help'))
const WhatsNew            = lazyWithRetry(() => import('./pages/WhatsNew'))
const HelpArticle         = lazyWithRetry(() => import('./pages/HelpArticle'))
const PrivacyPolicy       = lazyWithRetry(() => import('./pages/PrivacyPolicy'))

// ── Client portal (standalone — no admin sidebar/topbar) ──────────────────────
const PortalHome          = lazyWithRetry(() => import('./pages/portal/PortalHome'))
const PortalSubmit        = lazyWithRetry(() => import('./pages/portal/PortalSubmit'))
const PortalRequests      = lazyWithRetry(() => import('./pages/portal/PortalRequests'))
const PortalRequestDetail = lazyWithRetry(() => import('./pages/portal/PortalRequestDetail'))
const PortalHelp          = lazyWithRetry(() => import('./pages/portal/PortalHelp'))
const PortalHelpArticle   = lazyWithRetry(() => import('./pages/portal/PortalHelpArticle'))

const DEV_BYPASS     = import.meta.env.VITE_BYPASS_AUTH === 'true'
const USE_KEYCLOAK   = !DEV_BYPASS && !!import.meta.env.VITE_KEYCLOCK_URL

// ── Dev bypass — seed a mock admin so role-gated UI renders correctly ─────────
if (DEV_BYPASS) {
  const stored = localStorage.getItem('multiaccess-app')
  const parsed = stored ? JSON.parse(stored) : {}
  if (!parsed?.state?.token) {
    localStorage.setItem('multiaccess-app', JSON.stringify({
      ...parsed,
      state: {
        ...(parsed?.state ?? {}),
        token: 'dev-bypass-token',
        user: { id: 'dev-user', email: 'dev@multiaccess.local', name: 'Dev User', role: 'admin', orgId: 'dev-org', createdAt: '', updatedAt: '' },
      },
    }))
  }
}

// ── Keycloak auth gate ────────────────────────────────────────────────────────
// When VITE_KEYCLOCK_URL is set and bypass is off, this component owns auth.
// It renders a full-screen loading/error state while Keycloak initialises,
// then yields to children once a token is available.
function KeycloakGate({ children }: { children: React.ReactNode }) {
  const { ready, token, orgId, error, refresh } = useKeycloakAuth()
  const { setAuth, setToken, updateCurrentUser, clearAuth } = useAppStore()
  const syncedIdentityRef = useRef<string | null>(null)
  const location = useLocation()

  // client_individual / client_org_user only ever exist to use the client
  // portal — block them from the staff app entirely, even with a valid
  // Keycloak session for this realm, rather than letting them land on a
  // dashboard built for staff. Computed straight from the token's own roles
  // claim (decodeIdentity), so it's known synchronously, before ever calling
  // setAuth — a mixed account with a staff role too is left alone.
  const isClientOnlyAccount = useMemo(() => {
    if (!token) return false
    try {
      const roles = decodeIdentity(token).roleName
      return roles.length > 0 && roles.every(r => r === 'client_individual' || r === 'client_org_user')
    } catch {
      return false
    }
  }, [token])

  // Sync Keycloak identity into Zustand so the rest of the app (TopBar, etc.)
  // can read user.name / user.orgId without knowing about Keycloak directly.
  // Gated on `token` alone (not `orgId` too) — a brand-new user with no org
  // yet still has a valid token and needs their name/email/roles populated.
  useEffect(() => {
    if (token && !isClientOnlyAccount) {
      try {
        const identity = decodeIdentity(token)
        // Keyed on who the user is, not the token itself. Keycloak refreshes
        // the token every few minutes; rebuilding `user` on each refresh used
        // to drop roleName (from IAM) and subscribedServices until they were
        // refetched, so AppShell briefly failed its access checks, unmounted
        // the current page and lost its state. Same identity → token only.
        const syncKey = `${identity.id}:${orgId}`
        if (syncedIdentityRef.current === syncKey) {
          setToken(token)
          return
        }
        syncedIdentityRef.current = syncKey

        setAuth({
          id:        identity.id,
          name:      identity.name,
          email:     identity.email,
          role:      identity.roleName.includes('iam_superadmin') || identity.roleName.includes('iam_org_admin') ? 'admin' : 'viewer',
          roleName:  identity.roleName,
          orgId:     orgId ?? '',
          createdAt: '',
          updatedAt: '',
        }, token)
        // `role` above is a legacy Keycloak-realm-role flag, not this app's
        // actual custom roles (iam_org_admin, iam_superadmin, ...) — fetch the
        // real ones so useRole() and permission-gated UI work correctly.
        iamService.getCurrentUser()
          .then(profile => {
            if ((profile.roleName ?? []).length > 0) updateCurrentUser({ roleName: profile.roleName })
          })
          .catch(() => undefined)
        // Subscribed services gate Sidebar/route rendering (see isSubscribedPath
        // in utils/permissions.ts) — fetched once here, same as roleName above,
        // rather than per-page like Home.tsx's own org-profile query used to.
        // Keep the organization's literal module selection. Billing state must
        // not broaden the navigation beyond the services the org subscribed to.
        if (orgId) {
          iamAdminApi.getOrganization(orgId)
            .then(org => updateCurrentUser({ subscribedServices: org.subscribedServices ?? [] }))
            .catch(() => undefined)
        }
      } catch {
        syncedIdentityRef.current = null
        /* token decode failed — app still works, just no name in TopBar */
      }
    }
  }, [token, orgId, setAuth, setToken, updateCurrentUser, isClientOnlyAccount])

  if (!ready) {
    return (
      <div className="fixed inset-0 flex flex-col items-center justify-center gap-4 bg-bg-base">
        <div className="w-10 h-10 rounded-full border-2 border-brand-500/20 border-t-brand-500 animate-spin" />
        <p className="text-sm text-text-secondary">Connecting to authentication service…</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="fixed inset-0 flex flex-col items-center justify-center gap-4 bg-bg-base px-6 text-center">
        <div className="w-12 h-12 rounded-full bg-status-error/10 flex items-center justify-center">
          <span className="text-status-error text-xl">✕</span>
        </div>
        <p className="text-sm font-medium text-text-primary">{error}</p>
        <div className="flex gap-3">
          <button onClick={() => window.location.reload()} className="px-4 py-2 text-sm bg-brand-500 text-white rounded-lg hover:bg-brand-600 transition-colors">
            Retry
          </button>
          <button onClick={() => { clearAuth(); keycloakLogout() }} className="px-4 py-2 text-sm border border-border-default text-text-secondary rounded-lg hover:border-border-strong transition-colors">
            Logout
          </button>
        </div>
      </div>
    )
  }

  // Keycloak uses login-required — if we reach here with no token, KC will redirect.
  // This state is transient; render nothing while the redirect is in-flight.
  if (!token) return null

  if (isClientOnlyAccount) {
    return (
      <div className="fixed inset-0 flex flex-col items-center justify-center gap-4 bg-bg-base px-6 text-center">
        <div className="w-12 h-12 rounded-full bg-status-warning/10 flex items-center justify-center">
          <span className="text-status-warning text-xl">⚠</span>
        </div>
        <p className="text-sm font-medium text-text-primary">This is a client portal account</p>
        <p className="text-sm text-text-secondary max-w-sm">Client accounts sign in through the client portal, not the staff application. Please use the link below instead.</p>
        <div className="flex gap-3">
          <a href={`https://${import.meta.env.VITE_PORTAL_BASE_DOMAIN ?? 'portal.multiaccess.in'}`}
            className="px-4 py-2 text-sm bg-brand-500 text-white rounded-lg hover:bg-brand-600 transition-colors">
            Go to client portal
          </a>
          <button onClick={() => { clearAuth(); keycloakLogout() }} className="px-4 py-2 text-sm border border-border-default text-text-secondary rounded-lg hover:border-border-strong transition-colors">
            Logout
          </button>
        </div>
      </div>
    )
  }

  // New user with no org yet — route to setup instead of an empty dashboard.
  // Excludes /portal: client-portal users (client_individual/client_org_user)
  // are a different population from staff setting up a brand-new org.
  if (!orgId && !location.pathname.startsWith('/portal')) {
    return (
      <Suspense fallback={<PageLoader />}>
        <OnboardingWizard onOrgCreated={refresh} />
      </Suspense>
    )
  }

  return <>{children}</>
}

// ── Portal subdomain auth gate (Direct Access Grant) ──────────────────────────
function PortalAuthGate({ slug, children }: { slug: string; children: React.ReactNode }) {
  const { ready, token, branding, error, login, logout } = usePortalAuth(slug)

  if (!ready) {
    return (
      <div className="fixed inset-0 flex items-center justify-center" style={{ background: '#f1f5f9' }}>
        <div className="w-8 h-8 rounded-full border-2 border-gray-300 border-t-gray-500 animate-spin" />
      </div>
    )
  }

  if (!token) {
    return <PortalLogin branding={branding} error={error} onLogin={login} />
  }

  return <PortalAuthContext.Provider value={{ logout }}>{children}</PortalAuthContext.Provider>
}

// ── Custom-login auth guard (non-Keycloak path) ───────────────────────────────
function RequireAuth({ children }: { children: React.ReactNode }) {
  const { token, user } = useAppStore()
  const location        = useLocation()
  if (!DEV_BYPASS && !token) return <Navigate to="/login" state={{ from: location }} replace />
  if (!DEV_BYPASS && token && user && !user.orgId) return <Navigate to="/onboarding" replace />
  return <>{children}</>
}

function PageLoader() {
  return (
    <div className="flex items-center justify-center h-full py-24">
      <div className="w-8 h-8 rounded-full border-2 border-brand-500/20 border-t-brand-500 animate-spin" />
    </div>
  )
}

class RouteErrorBoundary extends Component<
  { children: ReactNode; resetKey: string },
  { error: Error | null }
> {
  state: { error: Error | null } = { error: null }

  static getDerivedStateFromError(error: Error) {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[route]', error, info)
  }

  componentDidUpdate(prevProps: { resetKey: string }) {
    if (prevProps.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({ error: null })
    }
  }

  render() {
    if (!this.state.error) return this.props.children

    return (
      <div className="flex h-full items-center justify-center px-6">
        <div className="max-w-lg rounded-xl border border-border bg-bg-subtle p-6 shadow-sm">
          <h1 className="text-base font-semibold text-text-primary">Page failed to load</h1>
          <p className="mt-2 text-sm text-text-secondary">
            Something on this page failed while rendering. Refresh the page, or use the details below when checking the browser console.
          </p>
          <pre className="mt-4 max-h-40 overflow-auto rounded-lg border border-border bg-bg-base p-3 text-xs text-text-secondary">
            {this.state.error.message}
          </pre>
          <button
            onClick={() => this.setState({ error: null })}
            className="mt-4 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-600"
          >
            Try again
          </button>
        </div>
      </div>
    )
  }
}

function AccessDenied() {
  return (
    <div className="flex h-full items-center justify-center px-6">
      <div className="max-w-md rounded-xl border border-border bg-bg-subtle p-6 text-center shadow-sm">
        <h1 className="text-base font-semibold text-text-primary">Access restricted</h1>
        <p className="mt-2 text-sm text-text-secondary">Your current role does not allow access to this page. Contact your organization admin if this looks incorrect.</p>
      </div>
    </div>
  )
}

function BrandingLoader() {
  const { token, user, setBranding } = useAppStore()

  useEffect(() => {
    if (!token || !user?.orgId) return
    let cancelled = false
    brandingApi.get()
      .then(b => {
        if (cancelled) return
        setBranding(b)
        applyBranding(b)
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [token, user?.orgId, setBranding])

  return null
}

// Live Chat's WebSocket connection registers the agent into multichannel's
// round-robin support pool server-side, regardless of role — so it must only
// ever be opened for roles that can actually reach the Live Chat page
// (canAccessPath('/tickets/live-chat')), never unconditionally for every
// staff user, or unrelated staff (HR/billing-only admins, etc.) would start
// getting customer chats assigned to them.
function MaybeSupportChat({ enabled, children }: { enabled: boolean; children: React.ReactNode }) {
  if (!enabled) return <>{children}</>
  return <SupportChatProvider>{children}</SupportChatProvider>
}

function AppShell() {
  const location = useLocation()
  const { user } = useAppStore()
  const { canAccessPath, hasAnyRole } = useRole()
  const waitingForRoles = USE_KEYCLOAK && !!user && user.roleName == null
  const canAccessCurrentPath = !waitingForRoles && canAccessPath(location.pathname)
    && isSubscribedPath(location.pathname, user?.roleName, user?.subscribedServices)
  // Contextual settings navigation keeps the originating form and its local
  // state mounted. Ordinary navigation continues to use the current location.
  const formReturn = location.state?.helpdeskFormReturn as Location | undefined
  const formBackground = location.pathname === '/tickets/settings' && formReturn
    && (['/tickets', '/tickets/scheduled', '/tickets/live-chat'].includes(formReturn.pathname)
      || /^\/tickets\/[^/]+$/.test(formReturn.pathname) && !['/tickets/settings', '/tickets/analytics'].includes(formReturn.pathname))
    && canAccessPath(formReturn.pathname)
    && isSubscribedPath(formReturn.pathname, user?.roleName, user?.subscribedServices)
    ? formReturn : undefined
  const canUseSupportChat = !waitingForRoles && canAccessPath('/tickets/live-chat')
  // Floating bubble is the convenience surface for agents who actively work
  // chats day-to-day — narrower than who can merely view the inbox page.
  const showSupportBubble = canUseSupportChat && hasAnyRole(['ticketing_admin', 'ticketing_agent', 'lead_agent'])

  return (
    <MaybeSupportChat enabled={canUseSupportChat}>
    <div className="flex h-screen overflow-hidden bg-bg-base text-text-primary">
      <BrandingLoader />
      <Sidebar />
      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
        <TopBar />
        <main className="flex-1 overflow-auto">
          <RouteErrorBoundary resetKey={location.pathname}>
            <Suspense fallback={<PageLoader />}>
              {waitingForRoles ? <PageLoader /> : canAccessCurrentPath ? <>
              <div style={{ display: formBackground ? 'none' : 'contents' }}>
              <Routes location={formBackground ?? location}>
              {/* ── Observability (existing) ── */}
              <Route path="/"                       element={<Home />} />
              <Route path="/dashboards"             element={<Dashboards />} />
              <Route path="/dashboards/:id"         element={<DashboardView />} />
              <Route path="/metrics"                element={<MetricsExplorer />} />
              <Route path="/logs"                   element={<LogsExplorer />} />
              <Route path="/traces"                 element={<TraceExplorer />} />
              <Route path="/query"                  element={<QueryBuilder />} />
              <Route path="/analytics"              element={<AnalyticsBuilder />} />
              <Route path="/correlations"           element={<Correlations />} />
              <Route path="/correlations/rules"     element={<CorrelationRules />} />
              <Route path="/correlations/incidents" element={<GroupedIncidents />} />
              <Route path="/ai"                     element={<AIInsights />} />
              <Route path="/alerts/rules"           element={<AlertRules />} />
              <Route path="/alerts/channels"        element={<AlertChannels />} />
              <Route path="/alerts/incidents"       element={<IncidentHistory />} />
              <Route path="/incidents/:id"          element={<IncidentDetail />} />
              <Route path="/slos"                   element={<Slos />} />
              <Route path="/slos/:id"               element={<SloDetail />} />
              <Route path="/runbooks"               element={<Runbooks />} />
              <Route path="/reports"                element={<Reports />} />
              <Route path="/teams"                  element={<TeamCollaboration />} />
              <Route path="/automation"             element={<Automation />} />
              <Route path="/plugins"                element={<PluginMarketplace />} />
              <Route path="/admin/datasources"      element={<DatasourceManager />} />
              <Route path="/admin/organization"     element={<OrganizationHub />} />
              <Route path="/admin/users"            element={<Navigate to="/admin/organization" replace />} />
              <Route path="/admin/audit"            element={<AuditLogs />} />
              <Route path="/admin/billing"          element={<Billing />} />
              <Route path="/admin/auth"             element={<EnterpriseAuth />} />
              <Route path="/admin/identity-providers" element={<Navigate to="/admin/auth" replace />} />
              <Route path="/admin/rbac"             element={<Navigate to="/admin/organization" replace />} />
              <Route path="/admin/branding"         element={<WhiteLabel />} />
              <Route path="/admin/ai-provider"      element={<AdminAIProvider />} />
              <Route path="/profile"                element={<Profile />} />
              <Route path="/settings"               element={<Settings />} />

              {/* ── Helpdesk ── */}
              <Route path="/tickets"             element={<Tickets />} />
              <Route path="/tickets/analytics"   element={<TicketAnalytics />} />
              <Route path="/tickets/settings"    element={<TicketSettings />} />
              <Route path="/tickets/live-chat"   element={<LiveChat />} />
              <Route path="/tickets/scheduled"   element={<ScheduleTicket />} />
              <Route path="/tickets/:id"         element={<TicketDetail />} />
              <Route path="/tasks"               element={<TaskBoard />} />
              <Route path="/tasks/planning"      element={<TaskPlanning />} />
              <Route path="/tasks/timesheets"    element={<Timesheets />} />
              <Route path="/tasks/timesheets/approvals" element={<TimesheetApprovals />} />
              <Route path="/notes"               element={<Notes />} />
              <Route path="/clients"       element={<Clients />} />
              <Route path="/clients/:id"   element={<ClientDetail />} />

              {/* ── Waste Management ── */}
              <Route path="/waste"                   element={<WasteOverview />} />
              <Route path="/waste/collections"       element={<WasteCollections />} />
              <Route path="/waste/facilities"        element={<WasteFacilities />} />
              <Route path="/waste/facilitators"      element={<WasteFacilitators />} />
              <Route path="/waste/simulator"         element={<WasteSimulator />} />
              <Route path="/waste/oversight"         element={<WasteOversight />} />
              <Route path="/waste/reports"           element={<WasteReports />} />
              <Route path="/waste/settings"          element={<WasteSettings />} />
              <Route path="/waste/pickups"           element={<WastePickups />} />
              <Route path="/waste/agencies"          element={<WasteAgencies />} />
              <Route path="/waste/marketplace"       element={<WasteMarketplace />} />
              <Route path="/waste/recyclers"         element={<WasteRecyclers />} />

              {/* ── Business ── */}
              <Route path="/invoices"                element={<Invoices />} />
              <Route path="/invoices/new"            element={<InvoiceCreate />} />
              <Route path="/invoices/settings"       element={<InvoiceSettings />} />
              <Route path="/invoices/:id/edit"       element={<InvoiceCreate />} />
              <Route path="/invoices/:id"            element={<InvoiceDetail />} />
              <Route path="/assets"                  element={<Assets />} />
              <Route path="/assets/categories"       element={<AssetCategories />} />
              <Route path="/assets/locations"        element={<AssetLocations />} />
              <Route path="/assets/manufacturers"    element={<AssetManufacturers />} />
              <Route path="/assets/vendors"          element={<AssetVendors />} />
              <Route path="/assets/components"       element={<AssetComponents />} />
              <Route path="/assets/allocations"      element={<AssetAllocations />} />
              <Route path="/assets/profiles"         element={<AssetProfiles />} />
              <Route path="/assets/workflows"        element={<AssetWorkflows />} />
              <Route path="/assets/saas-applications" element={<AssetSaaSApplications />} />
              <Route path="/assets/cloud-resources"   element={<AssetCloudResources />} />
              <Route path="/assets/credentials"       element={<AssetCredentials />} />
              <Route path="/assets/integrations"     element={<AssetIntegrations />} />
              <Route path="/assets/notifications"    element={<AssetNotifications />} />
              <Route path="/assets/agents"           element={<AssetAgents />} />
              <Route path="/assets/agents/:deviceId" element={<AgentDeviceDetail />} />
              <Route path="/assets/analytics"        element={<AssetAnalytics />} />
              <Route path="/assets/audits"           element={<AssetAudits />} />
              <Route path="/assets/audits/:cycleId"  element={<AssetAuditDetail />} />
              <Route path="/assets/reorder-policies" element={<AssetReorderPolicies />} />
              <Route path="/assets/reports"          element={<AssetReportBuilder />} />
              <Route path="/process-workflows"       element={<ProcessWorkflows />} />
              <Route path="/process-workflows/runs"  element={<ProcessRuns />} />
              <Route path="/assets/:id"              element={<AssetDetail />} />
              <Route path="/assets/:id/graph"        element={<AssetGraph />} />
              <Route path="/assets/requests"         element={<AssetRequests />} />
              <Route path="/assets/software-catalog" element={<AssetSoftwareCatalog />} />
              <Route path="/assets/scan"             element={<AssetScan />} />
              <Route path="/assets/network-topology" element={<AssetNetworkTopology />} />
              <Route path="/assets/contracts"        element={<Contracts />} />
              <Route path="/assets/purchase-orders"  element={<PurchaseOrders />} />
              <Route path="/assets/purchase-orders/procurement/new"      element={<ProcurementPOCreate />} />
              <Route path="/assets/purchase-orders/procurement/:id/edit" element={<ProcurementPOCreate />} />
              <Route path="/assets/purchase-orders/:id" element={<AssetPurchaseOrderDetail />} />

              {/* ── Quote-to-PO flow ── */}
              <Route path="/quotations"              element={<Quotations />} />
              <Route path="/quotations/new"          element={<QuotationCreate />} />
              <Route path="/quotations/:id/edit"     element={<QuotationCreate />} />
              <Route path="/quotations/:id"          element={<QuotationDetail />} />
              <Route path="/proforma-invoices"           element={<ProformaInvoices />} />
              <Route path="/proforma-invoices/new"       element={<ProformaCreate />} />
              <Route path="/proforma-invoices/:id/edit"  element={<ProformaCreate />} />
              <Route path="/proforma-invoices/:id"       element={<ProformaDetail />} />
              <Route path="/sales-orders"            element={<SalesOrders />} />
              <Route path="/sales-orders/new"        element={<SalesOrderCreate />} />
              <Route path="/sales-orders/:id/edit"   element={<SalesOrderCreate />} />
              <Route path="/sales-orders/:id"        element={<SalesOrderDetail />} />
              <Route path="/purchase-orders"                       element={<PurchaseOrdersBiz />} />
              <Route path="/purchase-orders/new"               element={<PurchaseOrderCreate />} />
              <Route path="/purchase-orders/:id/edit"          element={<PurchaseOrderCreate />} />
              <Route path="/purchase-orders/:id"               element={<PurchaseOrderDetail />} />
              <Route path="/purchase-orders/procurement/new"        element={<Navigate to="/assets/purchase-orders/procurement/new" replace />} />
              <Route path="/purchase-orders/procurement/:id/edit"   element={<Navigate to="/assets/purchase-orders" replace />} />

              {/* ── Knowledge Base ── */}
              <Route path="/kb"            element={<KnowledgeBase />} />
              <Route path="/kb/new"        element={<KBArticleEditor />} />
              <Route path="/kb/:id/edit"   element={<KBArticleEditor />} />
              <Route path="/kb/:id"        element={<KBArticle />} />

              {/* ── Help & Support ── */}
              <Route path="/help"              element={<Help />} />
              <Route path="/whats-new"         element={<WhatsNew />} />
              <Route path="/help/articles/:id" element={<HelpArticle />} />
              </Routes>
              </div>
              {formBackground && <Routes location={location}>
                <Route path="/tickets/settings" element={<TicketSettings />} />
              </Routes>}
              </> : <AccessDenied />}
            </Suspense>
          </RouteErrorBoundary>
        </main>
      </div>
      <CommandPalette />
      {showSupportBubble && <SupportChatBubble />}
    </div>
    </MaybeSupportChat>
  )
}

export default function App() {
  const { openCommandPalette } = useAppStore()
  const location = useLocation()

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        openCommandPalette()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [openCommandPalette])

  // When Keycloak is active: KC owns the full auth lifecycle (redirect to login,
  // token refresh). The custom /login route is irrelevant — wrap everything in
  // KeycloakGate instead of RequireAuth.
  const portalRoutes = (
    <Route element={<PortalLayoutRoute />}>
      <Route path="/portal"                    element={<PortalHome />} />
      <Route path="/portal/submit"             element={<PortalSubmit />} />
      <Route path="/portal/submit/:templateId" element={<PortalSubmit />} />
      <Route path="/portal/requests"           element={<PortalRequests />} />
      <Route path="/portal/requests/:id"       element={<PortalRequestDetail />} />
      <Route path="/portal/help"               element={<PortalHelp />} />
      <Route path="/portal/help/:id"           element={<PortalHelpArticle />} />
    </Route>
  )

  if (location.pathname === '/privacy' || location.pathname === '/data-deletion') {
    return (
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/data-deletion" element={<PrivacyPolicy />} />
        </Routes>
      </Suspense>
    )
  }

  // Client portal domain — either the bare portal.multiaccess.in (no org
  // pre-selected) or a per-org branded subdomain (acme.portal.multiaccess.in).
  // Entirely separate auth (custom branded login via Direct Access Grant, see
  // usePortalAuth) and route tree. Staff routes are never reachable here.
  // '' (bare domain) is still portal mode, so check against null specifically.
  const portalSlug = getPortalSlugFromHost()
  if (portalSlug !== null) {
    return (
      <PortalAuthGate slug={portalSlug}>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {portalRoutes}
            <Route path="*" element={<Navigate to="/portal" replace />} />
          </Routes>
        </Suspense>
      </PortalAuthGate>
    )
  }

  if (USE_KEYCLOAK) {
    return (
      <KeycloakGate>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {portalRoutes}
            <Route path="/*" element={<AppShell />} />
          </Routes>
        </Suspense>
      </KeycloakGate>
    )
  }

  return (
    <Suspense fallback={<PageLoader />}>
    <Routes>
      <Route path="/login"      element={<Login />} />
      <Route path="/register"   element={<Register />} />
      <Route path="/onboarding" element={<OnboardingWizard />} />
      {portalRoutes}
      <Route path="/*" element={
        <RequireAuth>
          <AppShell />
        </RequireAuth>
      } />
    </Routes>
    </Suspense>
  )
}
